import {
  type ChipColorTypes,
  type ChipKindTypes,
  type IconKind,
  type MenuOption,
} from '@saviynt/design-system';
import { type IntlShape } from 'react-intl';

import { type AgentOwner, type CriticalityLevel } from '../component';
import {
  EDIT_OWNER_TABLE_COLUMN_CONFIG,
  EDIT_OWNER_TABLE_COLUMN_KEY_1,
  EDIT_OWNER_TABLE_COLUMN_KEY_2,
  EDIT_OWNER_TABLE_COLUMN_KEY_3,
  EDIT_OWNERS_RANK,
  EDIT_OWNERS_ROLE_OPTIONS,
  FILTERS_CONFIG,
  LABEL_CRITICAL_VALIDATION,
  LABEL_CRITICAL_VALIDATION_MSG,
  LABEL_TABLE_COLUMN_CONFIG,
  LABEL_TABLE_COLUMN_KEY_1,
  LABEL_TABLE_COLUMN_KEY_2,
  LIST_COLUMNS,
  managePageMessages,
  MODEL_CONFIG,
  OWNER_TABLE_COLUMN_CONFIG,
  OWNER_TABLE_COLUMN_KEY_1,
  OWNER_TABLE_COLUMN_KEY_2,
  OWNER_TABLE_COLUMN_KEY_3,
  OWNER_TABLE_COLUMN_KEY_4,
  PLATFORM_CONFIG,
  STATUS_CONFIG,
} from '../constants/managepage';
import { type FilterState } from '../contexts/ManagePageContext';

// Common;
export const formatDateWithTime = (
  dateValue: string | Date | null | undefined,
  format: string
): string | null | undefined => {
  if (!dateValue) {
    return dateValue;
  }

  try {
    let parsedValue: string | Date = dateValue;

    if (typeof dateValue === 'string') {
      parsedValue = dateValue
        .replace(/\.(\d{3})\d+/, '.$1') // trim microseconds
        .replace(/Z?$/, 'Z'); // ensure UTC timezone
    }

    const date = new Date(parsedValue);

    if (isNaN(date.getTime())) {
      return String(dateValue);
    }

    const monthShort = date.toLocaleString('en-US', {
      month: 'short',
      timeZone: 'UTC',
    });
    const monthFull = date.toLocaleString('en-US', {
      month: 'long',
      timeZone: 'UTC',
    });

    let hours = date.getHours();
    const day = date.getDate();
    const year = date.getFullYear();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const time = `${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;

    // Token-based replacement (case-sensitive)
    const tokenMap: Record<string, string> = {
      yyyy: String(year),
      YYYY: String(year),
      yy: String(year).slice(-2),
      YY: String(year).slice(-2),
      MMMM: monthFull,
      MMM: monthShort,
      MM: String(date.getMonth() + 1).padStart(2, '0'),
      dd: String(day).padStart(2, '0'),
      DD: String(day).padStart(2, '0'),
      d: String(day),
      D: String(day),
    };

    // Replace tokens longest-first to avoid partial matches
    const tokenPattern = Object.keys(tokenMap)
      .sort((a, b) => b.length - a.length)
      .join('|');

    const formattedDate = format.replace(
      new RegExp(tokenPattern, 'g'),
      (match) => tokenMap[match] ?? match
    );

    return `${formattedDate} ${time}`;
  } catch {
    return String(dateValue);
  }
};

export const getSortOptions = (intl: IntlShape) =>
  [
    {
      label: intl.formatMessage({
        defaultMessage: 'Ascending',
        id: '3O5viX',
        description: 'sort option: Ascending',
      }),
      value: 'asc',
      icon: 'ArrowUp' as IconKind,
      isDisabled: false,
      isChecked: false,
      shouldShowCheckIcon: true,
    },
    {
      label: intl.formatMessage({
        defaultMessage: 'Descending',
        id: 'L24fRI',
        description: 'sort option: Descending',
      }),
      value: 'desc',
      icon: 'ArrowDown' as IconKind,
      isDisabled: false,
      isChecked: false,
      shouldShowCheckIcon: true,
    },
  ].map((o) => ({ ...o, isChecked: false }));

// Filters
export const getFilterConfig = (intl: IntlShape) =>
  FILTERS_CONFIG.map((f) => ({
    ...f,
    label: intl.formatMessage(managePageMessages[f.messageLabelKey]),
    options: [],
    isLoading: false,
    isError: false,
    hasMoreOptions: true,
  }));

export const getSearchValue = (filters: FilterState[], targetKey: string) =>
  filters.find((f) => f.value === targetKey)?.searchText ?? '';

export const getNoDataText = <T extends boolean>(
  intl: IntlShape,
  isError?: T,
  isLoading?: T,
  searchText?: string | null
) => {
  if (isError) {
    return intl.formatMessage({
      id: 'Aumxx3',
      defaultMessage: 'Search failed; please try again',
      description: 'Search failed; please try again',
    });
  }

  if (isLoading) {
    return intl.formatMessage({
      id: 'd1eDsY',
      defaultMessage: 'Loading...',
      description: 'Loading...',
    });
  }

  if (!searchText) {
    return intl.formatMessage({
      id: 'pQc/cw',
      defaultMessage: 'Enter at least 1 character to view options.',
      description: 'Enter at least 1 character to view options.',
    });
  }

  return undefined;
};

// Lists
export const getListColumns = (intl: IntlShape) =>
  LIST_COLUMNS.map((col) => ({
    ...col,
    label: intl.formatMessage(managePageMessages[col.messageLabelKey]),
    options: getSortOptions(intl),
    isChecked: true,
  }));

export const getPlatformLogo = (platform: string | null) => {
  if (!platform) {
    return 'Application';
  }

  const normalized = platform.replace(/[^\p{L}\p{N}]/gu, '').toLowerCase();

  const match = PLATFORM_CONFIG.find(({ match }) => match(normalized));

  return match?.logo ?? 'Application';
};

export const getModelLogo = (model: string | null | undefined) => {
  if (!model) {
    return 'Application';
  }

  const normalized = model.replace(/[^\p{L}\p{N}]/gu, '').toLowerCase();

  const match = MODEL_CONFIG.find(({ match }) => match(normalized));

  return match?.logo ?? 'Application';
};

export const getCriticalityProps = (
  criticality: CriticalityLevel | null
): {
  kind: ChipKindTypes;
  color: ChipColorTypes;
} => {
  if (criticality?.toLowerCase() === 'critical') {
    return {
      kind: 'boldFilled',
      color: 'critical',
    };
  }

  if (criticality?.toLowerCase() === 'high') {
    return {
      kind: 'subtleFilled',
      color: 'critical',
    };
  }

  if (criticality?.toLowerCase() === 'medium') {
    return {
      kind: 'subtleFilled',
      color: 'warning',
    };
  }

  if (criticality?.toLowerCase() === 'low') {
    return {
      kind: 'subtleFilled',
      color: 'success',
    };
  }

  return {
    kind: 'subtleFilled',
    color: 'neutral',
  };
};

export const getStatusProps = (
  status: string | null
): {
  kind: ChipKindTypes;
  color: ChipColorTypes;
} => {
  if (!status) {
    return { kind: 'subtleFilled', color: 'neutral' };
  }

  const normalized = status.toLowerCase();
  const match = STATUS_CONFIG.find(({ match }) => match(normalized));

  return {
    kind: 'subtleFilled',
    color: match?.color ?? 'neutral',
  };
};

// Sidepanel
export const getLabelTabColConfig = (intl: IntlShape) =>
  LABEL_TABLE_COLUMN_CONFIG.map((col) => ({
    ...col,
    label: intl.formatMessage(managePageMessages[col.messageLabelKey]),
    isChecked: true,
    isSortable: true,
  }));

export const getLabelTabSortConfig = (intl: IntlShape) => [
  { key: LABEL_TABLE_COLUMN_KEY_1, sortOptions: getSortOptions(intl) },
  { key: LABEL_TABLE_COLUMN_KEY_2, sortOptions: getSortOptions(intl) },
];

export const sortEditLabels = (
  labels: Record<string, string>,
  { sortBy, order }: { sortBy?: string; order?: 'asc' | 'desc' } = {}
) => {
  if (!sortBy || !order) {
    return labels;
  }

  try {
    const entries = Object.entries(labels);
    const sorted = entries.sort((a, b) => {
      let valA = sortBy === 'keys' ? a[0] : a[1];
      let valB = sortBy === 'keys' ? b[0] : b[1];

      valA = String(valA).toLowerCase();
      valB = String(valB).toLowerCase();

      if (valA < valB) {
        return order === 'asc' ? -1 : 1;
      }
      if (valA > valB) {
        return order === 'asc' ? 1 : -1;
      }

      return 0;
    });

    return Object.fromEntries(sorted);
  } catch {
    return labels;
  }
};

export const sortOwners = (
  owners: AgentOwner[],
  { sortBy, order }: { sortBy?: string; order?: 'asc' | 'desc' } = {}
) => {
  const keyMap: Record<string, (keyof AgentOwner)[]> = {
    name_type: ['name', 'type'],
    source: ['source'],
    role: ['role'],
    rank: ['rank'],
  };

  if (!sortBy || !order) {
    return owners;
  }

  try {
    const keys = keyMap[sortBy] || [];

    return [...owners].sort((a, b) => {
      for (const key of keys) {
        let valA = a[key];
        let valB = b[key];

        valA = String(valA).toLowerCase();
        valB = String(valB).toLowerCase();

        if (valA < valB) {
          return order === 'asc' ? -1 : 1;
        }
        if (valA > valB) {
          return order === 'asc' ? 1 : -1;
        }
      }

      return 0;
    });
  } catch {
    return owners;
  }
};

export const getOwnerTabColConfig = (intl: IntlShape) =>
  OWNER_TABLE_COLUMN_CONFIG.map((o) => ({
    ...o,
    label: intl.formatMessage(managePageMessages[o.messageLabelKey]),
    isChecked: true,
    isSortable: true,
  }));

export const getEditOwnerTabColConfig = (intl: IntlShape) =>
  EDIT_OWNER_TABLE_COLUMN_CONFIG.map((o) => ({
    ...o,
    label: intl.formatMessage(managePageMessages[o.messageLabelKey]),
    isChecked: true,
    isSortable: true,
  }));

export const getOwnerSortConfig = (intl: IntlShape) => [
  { key: OWNER_TABLE_COLUMN_KEY_1, sortOptions: getSortOptions(intl) },
  { key: OWNER_TABLE_COLUMN_KEY_2, sortOptions: getSortOptions(intl) },
  { key: OWNER_TABLE_COLUMN_KEY_3, sortOptions: getSortOptions(intl) },
  { key: OWNER_TABLE_COLUMN_KEY_4, sortOptions: getSortOptions(intl) },
];

export const getEditOwnerSortConfig = (intl: IntlShape) => [
  { key: EDIT_OWNER_TABLE_COLUMN_KEY_1, sortOptions: getSortOptions(intl) },
  { key: EDIT_OWNER_TABLE_COLUMN_KEY_2, sortOptions: getSortOptions(intl) },
  { key: EDIT_OWNER_TABLE_COLUMN_KEY_3, sortOptions: getSortOptions(intl) },
];

export const getOwnerRoleOptions = (intl: IntlShape): MenuOption[] =>
  EDIT_OWNERS_ROLE_OPTIONS.map((or) => ({
    ...or,
    label: intl.formatMessage(managePageMessages[or.messageLabelKey]),
  }));

export const getOwnerRankOptions = (intl: IntlShape): MenuOption[] =>
  EDIT_OWNERS_RANK.map((or) => ({
    ...or,
    label: intl.formatMessage(managePageMessages[or.messageLabelKey]),
  }));

export const capitalizeFirstLetter = (word: string | undefined | null) => {
  if (!word) {
    return word;
  }

  return word.charAt(0).toUpperCase() + word.slice(1);
};

export const formatTableValue = (value: string) =>
  value
    .split('-')
    .join('_')
    .split('_')
    .filter((w) => w.length > 0)
    .map((w) => capitalizeFirstLetter(w))
    .join(' ');

export const getIsCritical = (value: string | null) =>
  LABEL_CRITICAL_VALIDATION.some(({ match }) => match(value));

export const getIsKeyCritical = (value: string | null, currentKeys: string[]) =>
  Boolean(
    value !== null && (getIsCritical(value) || currentKeys.includes(value))
  );

export const getCriticalMessage = (value: string | null, intl: IntlShape) => {
  const rule = LABEL_CRITICAL_VALIDATION_MSG.find(({ match }) => match(value));

  return rule ? intl.formatMessage(managePageMessages[rule.message]) : '';
};

export const getKeyCriticalMessage = (
  value: string | null,
  currentKeys: string[],
  intl: IntlShape
) => {
  const currentMessage = getCriticalMessage(value, intl);

  if (
    value !== null &&
    currentMessage.trim().length === 0 &&
    currentKeys.includes(value)
  ) {
    return intl.formatMessage({
      id: 'G96yrV',
      defaultMessage: 'This key is already added to this agent.',
      description: 'This key is already added to this agent.',
    });
  }

  return currentMessage;
};

export const shallowEqual = <
  T extends Record<string, string> = Record<string, string>,
>(
  obj1: T,
  obj2: T
) => {
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (obj1[key] !== obj2[key]) {
      return false;
    }
  }

  return true;
};

export const areValidValues = (values: string[]) =>
  !values.map((v) => getIsCritical(v)).some((v) => v);

export const areValidKeys = (values: string[]) =>
  !values.map((v) => getIsCritical(v)).some((v) => v);

export const getOwnerIcon = (type: string | null) => {
  if (!type) {
    return 'User';
  }

  if (type.toLowerCase().includes('group')) {
    return 'Group';
  }

  return 'User';
};

export const getOwnerIconTooltip = (type: string | null, intl: IntlShape) => {
  if (!type) {
    return intl.formatMessage({
      id: 'iWCA9m',
      defaultMessage: 'User',
      description: 'User',
    });
  }

  if (type.toLowerCase().includes('group')) {
    return intl.formatMessage({
      id: 'lGcO12',
      defaultMessage: 'User Group',
      description: 'User Group',
    });
  }

  return intl.formatMessage({
    id: 'iWCA9m',
    defaultMessage: 'User',
    description: 'User',
  });
};
